/* @flow */
import * as React from "react";
import { Redirect, Route } from "react-router-dom";
import InternetRequiredRoute from "nexia_mobile/components/Routing/InternetRequiredRoute";
import Store from "nexia_mobile/flux_like/store";
import Dispatch from "nexia_mobile/flux_like/dispatch";
import LocalSettings, { AUTH_TYPES } from "nexia_mobile/models/local_settings";
import SwitchWith404 from "nexia_mobile/components/Routing/SwitchWith404";

import AppRoutes from "nexia_mobile/components/Routing/AppRoutes";
import ValidatePinEntryPage from "nexia_mobile/components/Pages/ValidatePinEntryPage";
import { makeLoaderComponent } from "nexia_mobile/components/Routing/Loading";

import { ConnectedPhoneContextWrapper } from "nx-components/ContextWrappers/PhoneContext";
import { UserLanguageContextWrapper } from "nx-components/ContextWrappers/UserLanguageContext";

const ActivatePhonePage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/ActivatePhonePage")
);
const ForgotCredentialsPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/ForgotCredentialsPage")
);
const SignUpPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/SignUpPage")
);
const CreatePinEntryPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/CreatePinEntryPage")
);
const ReauthenticateUserPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/ReauthenticateUserPage")
);
const SignInPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/SignInPage")
);
const WelcomePage = makeLoaderComponent(() => {
  SignInPage.preload();
  SignUpPage.preload();
  CreatePinEntryPage.preload();
  return import("nexia_mobile/components/Pages/WelcomePage");
});
const ResetPasswordPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/ResetPasswordPage")
);
const TransferAccountPage = makeLoaderComponent(() =>
  import("nexia_mobile/components/Pages/TransferAccountPage")
);

export default class RootRoutes extends React.Component<{}> {
  render() {
    return (
      <SwitchWith404>
        <Route
          path="/"
          exact
          render={() => {
            if (Store.appState.inDemoMode) {
              return <Redirect to="/app" />;
            }

            if (!LocalSettings.isActivated()) {
              return <Redirect to="/welcome" />;
            }
            Dispatch("POPULATE_SESSION");

            if (LocalSettings.needsPin()) {
              return <Redirect to="/pin-entry/create" />;
            }

            switch (LocalSettings.getCurrentAuth()) {
              case AUTH_TYPES.FINGERPRINT:
              case AUTH_TYPES.PIN: {
                return <Redirect to="/pin-entry/validate" />;
              }
              case AUTH_TYPES.NONE: {
                return <Redirect to="/app" />;
              }
              case AUTH_TYPES.UNINITIALIZED: {
                return <Redirect to="/pin-entry/create" />;
              }
              default: {
                // Should never get here
                return <Redirect to="/welcome" />;
              }
            }
          }}
        />
        <Route path="/welcome" exact component={WelcomePage} />
        <Route
          path="/pin-entry/create"
          exact
          render={({ location: { state } }) => (
            <CreatePinEntryPage isPinReset={state && state.isPinReset} />
          )}
        />
        <Route
          path="/pin-entry/validate"
          exact
          component={ValidatePinEntryPage}
        />
        <InternetRequiredRoute
          path="/reauth"
          exact
          render={({
            location: {
              state: {
                authenticationParams,
                prompt,
                submitButtonText,
                title,
                onUserAuthenticated,
              },
            },
          }) => (
            <ReauthenticateUserPage
              authenticationParams={authenticationParams}
              prompt={prompt}
              submitButtonText={submitButtonText}
              title={title}
              onUserAuthenticated={onUserAuthenticated}
            />
          )}
        />
        <Route path="/signin" exact component={SignInPage} />
        <Route
          path="/signin/activate-phone"
          exact
          component={ActivatePhonePage}
        />
        <InternetRequiredRoute
          path="/signin/forgot-credentials"
          exact
          component={ForgotCredentialsPage}
        />
        <InternetRequiredRoute
          path="/signup"
          // Not exact because SignUpPage has nested routes
          component={SignUpPage}
        />
        <Route path="/reset-password" exact component={ResetPasswordPage} />
        <InternetRequiredRoute
          path="/transfer-account/user-info"
          exact
          component={TransferAccountPage}
        />
        <InternetRequiredRoute
          path="/transfer-account/challenge-terms"
          exact
          component={TransferAccountPage}
        />
        <Route
          path="/app"
          // Not exact because AppRoutes has nested routes
          render={() => (
            <ConnectedPhoneContextWrapper>
              <UserLanguageContextWrapper>
                <AppRoutes />
              </UserLanguageContextWrapper>
            </ConnectedPhoneContextWrapper>
          )}
        />
      </SwitchWith404>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Routing/RootRoutes.jsx